// ==================== Application Form Builder Constants ====================
import { GENDER_OPTIONS as CANONICAL_GENDER_OPTIONS } from '@kit/shared/gender';

import type {
  ApplicationFieldType,
  ApplicationStatus,
  AuditionCloseReason,
  AuditionStatus,
  AuditionType,
  ContractLength,
} from '../types/audition.types';

export const AUDITION_STATUSES: {
  value: AuditionStatus;
  label: string;
  description: string;
}[] = [
  {
    value: 'draft',
    label: 'Draft',
    description: 'Not yet published, only visible to organization',
  },
  {
    value: 'open',
    label: 'Open',
    description: 'Accepting applications',
  },
  {
    value: 'closed',
    label: 'Closed',
    description: 'No longer accepting applications',
  },
  {
    value: 'archived',
    label: 'Archived',
    description: 'Hidden from all listings',
  },
];

/**
 * Human-readable labels for `auditions.close_reason` (why an audition left
 * `'open'`). Used on the organizer dashboard to distinguish "Filled" from
 * "Deadline passed". `i18nKey` maps into the `auditions` namespace.
 */
export const CLOSE_REASONS: {
  value: AuditionCloseReason;
  label: string;
  i18nKey: string;
}[] = [
  {
    value: 'filled',
    label: 'Filled',
    i18nKey: 'auditions:closeReason.filled',
  },
  {
    value: 'deadline_passed',
    label: 'Deadline passed',
    i18nKey: 'auditions:closeReason.deadlinePassed',
  },
  {
    value: 'event_passed',
    label: 'Event passed',
    i18nKey: 'auditions:closeReason.eventPassed',
  },
  {
    value: 'stale_import',
    label: 'Listing removed',
    i18nKey: 'auditions:closeReason.staleImport',
  },
  {
    value: 'manual',
    label: 'Archived',
    i18nKey: 'auditions:closeReason.manual',
  },
];

export const AUDITION_TYPES: {
  value: AuditionType;
  label: string;
  description: string;
}[] = [
  {
    value: 'public',
    label: 'Public',
    description: 'Open to all dancers',
  },
  {
    value: 'private',
    label: 'Private',
    description: 'By invitation only',
  },
];

export const CONTRACT_LENGTHS: {
  value: ContractLength;
  label: string;
  description: string;
}[] = [
  {
    value: 'life',
    label: 'Permanent',
    description: 'Indefinite contract',
  },
  {
    value: 'seasonal',
    label: 'Seasonal',
    description: 'Season-based contract (typically 9-10 months)',
  },
  {
    value: 'shortTerm',
    label: 'Short Term',
    description: 'Project or temporary engagement',
  },
];

export const APPLICATION_STATUSES: {
  value: ApplicationStatus;
  label: string;
  description: string;
  color: string;
}[] = [
  {
    value: 'open',
    label: 'In Progress',
    description: 'Application started but not submitted',
    color: 'gray',
  },
  {
    value: 'issues',
    label: 'Has Issue',
    description: 'Application has an issue that needs to be fixed',
    color: 'yellow',
  },
  {
    value: 'submitted',
    label: 'Submitted',
    description: 'Application submitted and awaiting review',
    color: 'blue',
  },
  {
    value: 'completed',
    label: 'Completed',
    description: 'Registration complete — no further action needed',
    color: 'green',
  },
  {
    value: 'accepted',
    label: 'Accepted',
    description: 'Application accepted by organization',
    color: 'green',
  },
  {
    value: 'rejected',
    label: 'Rejected',
    description: 'Application rejected by organization',
    color: 'red',
  },
  {
    value: 'archived',
    label: 'Archived',
    description: 'Application archived',
    color: 'gray',
  },
];

// Derived from the canonical gender registry (`@kit/shared/gender`) so the
// values match the `gender_type` enum stored in `requirement_genders`
// (`nonBinary`, camelCase). The previous hand-maintained list used
// `'non-binary'` (kebab) — a drifted parallel map that made the audition
// requirements checkboxes WRITE a value no reader could label and made the
// public view fall back to the raw enum value. `'all'` is an
// audition-requirements-only sentinel, not part of the enum.
export const GENDER_OPTIONS = [
  ...CANONICAL_GENDER_OPTIONS,
  { value: 'all', label: 'All genders' },
];

export const DANCE_POSITIONS = [
  { value: 'principal', label: 'Principal' },
  { value: 'soloist', label: 'Soloist' },
  { value: 'demi-soloist', label: 'Demi-Soloist' },
  { value: 'corps', label: 'Corps de Ballet' },
  { value: 'apprentice', label: 'Apprentice' },
  { value: 'trainee', label: 'Trainee' },
];

export const MEDIA_REQUIREMENTS = [
  { value: 'headshot', label: 'Headshot' },
  { value: 'full-body', label: 'Full Body Photo' },
  { value: 'ballet-position', label: 'Ballet Position Photo' },
  { value: 'contemporary-position', label: 'Contemporary Position Photo' },
  { value: 'video-reel', label: 'Video Reel' },
  { value: 'resume', label: 'Dance Resume/CV' },
];

export const CURRENCIES = [
  { value: 'USD', label: 'USD ($)', symbol: '$' },
  { value: 'EUR', label: 'EUR (\u20AC)', symbol: '\u20AC' },
  { value: 'GBP', label: 'GBP (\u00A3)', symbol: '\u00A3' },
  { value: 'CHF', label: 'CHF', symbol: 'CHF' },
];

// Page sizes for pagination
export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 100;

// Form limits
export const MAX_TITLE_LENGTH = 50;
export const MAX_DESCRIPTION_LENGTH = 5000;
export const MAX_AUDITION_DATES = 20;

/**
 * Available field types with metadata for the form builder UI
 */
export const APPLICATION_FIELD_TYPES: {
  value: ApplicationFieldType;
  label: string;
  description: string;
  icon: string;
  hasOptions: boolean;
  category: 'input' | 'selection' | 'media' | 'display';
}[] = [
  {
    value: 'text',
    label: 'Short Text',
    description: 'Single line text input',
    icon: 'Type',
    hasOptions: false,
    category: 'input',
  },
  {
    value: 'textarea',
    label: 'Long Text',
    description: 'Multi-line text input',
    icon: 'AlignLeft',
    hasOptions: false,
    category: 'input',
  },
  {
    value: 'number',
    label: 'Number',
    description: 'Numeric input',
    icon: 'Hash',
    hasOptions: false,
    category: 'input',
  },
  {
    value: 'email',
    label: 'Email',
    description: 'Email address input',
    icon: 'Mail',
    hasOptions: false,
    category: 'input',
  },
  {
    value: 'phone',
    label: 'Phone',
    description: 'Phone number input',
    icon: 'Phone',
    hasOptions: false,
    category: 'input',
  },
  {
    value: 'date',
    label: 'Date',
    description: 'Date picker',
    icon: 'Calendar',
    hasOptions: false,
    category: 'input',
  },
  {
    value: 'url',
    label: 'URL',
    description: 'Website link input',
    icon: 'Link',
    hasOptions: false,
    category: 'input',
  },
  {
    value: 'select',
    label: 'Dropdown',
    description: 'Single selection dropdown',
    icon: 'ChevronDown',
    hasOptions: true,
    category: 'selection',
  },
  {
    value: 'multiselect',
    label: 'Multi-Select',
    description: 'Multiple selection dropdown',
    icon: 'ListChecks',
    hasOptions: true,
    category: 'selection',
  },
  {
    value: 'checkbox',
    label: 'Checkbox',
    description: 'Single yes/no checkbox',
    icon: 'CheckSquare',
    hasOptions: false,
    category: 'selection',
  },
  {
    value: 'checkboxGroup',
    label: 'Checkbox Group',
    description: 'Multiple checkboxes',
    icon: 'SquareStack',
    hasOptions: true,
    category: 'selection',
  },
  {
    value: 'radio',
    label: 'Radio Buttons',
    description: 'Single selection with radio buttons',
    icon: 'Circle',
    hasOptions: true,
    category: 'selection',
  },
  {
    value: 'file',
    label: 'File Upload',
    description: 'Upload documents or images',
    icon: 'Upload',
    hasOptions: false,
    category: 'media',
  },
  {
    value: 'video',
    label: 'Video',
    description: 'Video upload or link',
    icon: 'Video',
    hasOptions: false,
    category: 'media',
  },
  {
    value: 'videoCategories',
    label: 'Video Categories',
    description: 'Fixed-category videos (a file or link per category)',
    icon: 'Video',
    // Categories are edited via the field options editor.
    hasOptions: true,
    category: 'media',
  },
  {
    value: 'experience',
    label: 'Experience',
    description: "Embedded experience editor (updates the dancer's profile)",
    icon: 'Briefcase',
    hasOptions: false,
    category: 'media',
  },
  {
    value: 'competition_styles',
    label: 'Competition Styles',
    description:
      "Multi-select of the competition's style entries (auto-sourced, not hand-listed)",
    icon: 'Sparkles',
    // Options are the competition's competition_categories rows, supplied at
    // render time — not editable as static field options.
    hasOptions: false,
    category: 'selection',
  },
  {
    value: 'age_category',
    label: 'Age Category',
    description:
      "Read-only age division, auto-computed from the dancer's date of birth",
    icon: 'CalendarClock',
    hasOptions: false,
    category: 'display',
  },
  {
    value: 'heading',
    label: 'Section Heading',
    description: 'Section title (display only)',
    icon: 'Heading',
    hasOptions: false,
    category: 'display',
  },
  {
    value: 'paragraph',
    label: 'Instructions',
    description: 'Instructional text (display only)',
    icon: 'FileText',
    hasOptions: false,
    category: 'display',
  },
];

/**
 * Get field type metadata
 */
export function getFieldTypeMetadata(type: ApplicationFieldType) {
  return APPLICATION_FIELD_TYPES.find((t) => t.value === type);
}
